import React, { useEffect, useState, useContext, useRef } from "react";
import { UserContext } from '../../contexts/UserContext';
import ProfilePictureUpload from '.././ProfilePictureUpload';
import { User, Plus, Building, Shield, Briefcase, ChevronDown, ChevronUp, Download, Eye, AlertTriangle, History } from 'lucide-react';
import { Formik, Field, Form, FieldArray } from "formik";
import * as Yup from "yup";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // 10MB in bytes

const AddOrEditUserForm = ({ isCreatorOpen, setIsCreatorOpen, editingUserId, onSave }) => {
  const { user } = useContext(UserContext);
  const fileInputRefs = useRef([]);
  const languageFileInputRefs = useRef([]);
  const [globalQualifications, setGlobalQualifications] = useState([]);
  const [qualificationCategories, setQualificationCategories] = useState([]);
  const [originalValues, setOriginalValues] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedSection, setSelectedSection] = useState("basic");
  const [openCategories, setOpenCategories] = useState({});
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingQualifications, setPendingQualifications] = useState([]);
  const [skipHistoricalWarning, setSkipHistoricalWarning] = useState(false);
  const [windaResultsModal, setWindaResultsModal] = useState({ open: false, data: null });
  const [qualificationHistory, setQualificationHistory] = useState({}); // { qualId: { loading, show, data } }
  const [previewLoading, setPreviewLoading] = useState(false);
  const formikRef = useRef(null);

  // Function to preview document with authorization
  const previewDocumentWithAuth = async (url, name) => {
    console.log("Preview requested for:", url, name);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      console.log("Fetching with token:", token ? "present" : "missing");

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("Response status:", response.status, response.statusText);

      if (response.ok) {
        const blob = await response.blob();
        console.log("Blob type:", blob.type, "size:", blob.size);
        const blobUrl = URL.createObjectURL(blob);
        setPreviewDocument({ url: blobUrl, name, isBlob: true, originalUrl: url });
        setShowDocumentPreview(true);
      } else {
        console.log("Auth failed, trying direct URL");
        // If auth fails, try direct URL (might be public)
        setPreviewDocument({ url, name, isBlob: false });
        setShowDocumentPreview(true);
      }
    } catch (error) {
      console.error("Error loading document:", error);
      // Fallback to direct URL
      setPreviewDocument({ url, name, isBlob: false });
      setShowDocumentPreview(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const [initialValues, setInitialValues] = useState({
    username: "",
    email: "",
    company_email: "",
    first_name: "",
    last_name: "",
    password: "",
    is_active: true,
    is_staff: false,
    is_company_admin: false,
    inspector: false,
    profile_picture: null,
    date_of_birth: "",
    phone_number: "",
    personal_address: "",
    closest_airport: '',
    winda_id: "",
    languages: [],
    company_address: "",
    tax_id: "",
    eu_tax_id: "",
    tax_id_fi: "",
    your_company_name: "",
    qualifications: [],
  });

  const LANGUAGE_LEVEL_CHOICES = [
    { value: 'a1', label: 'A1' },
    { value: 'a2', label: 'A2' },
    { value: 'b1', label: 'B1' },
    { value: 'b2', label: 'B2' },
    { value: 'c1', label: 'C1' },
    { value: 'c2', label: 'C2' },
  ];

  const getValidationSchema = (editingUserId) => {
    return Yup.object({
      username: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email").nullable(),
      company_email: Yup.string().email("Invalid company email").nullable(),
      first_name: Yup.string().required("Required"),
      last_name: Yup.string().required("Required"),
      password: editingUserId ? Yup.string() : Yup.string().required("Password is required"),
      confirm_password: editingUserId ? Yup.string() : Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
      languages: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().required("Language is required"),
            level: Yup.string().required("Level is required"),
            certificate: Yup.mixed().nullable(),
          })
        )
        .test(
          "unique-languages",
          "Duplicate languages are not allowed.",
          (languages) => {
            if (!languages || languages.length === 0) return true;
            const uniqueLanguages = new Set(languages.map((lang) => lang.name));
            return uniqueLanguages.size === languages.length;
          }
        )
        .nullable(),
    });
  };

  useEffect(() => {
    setSelectedSection(editingUserId ? "personal" : "basic");
  }, [editingUserId]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(`${API_BASE_URL}/languages/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Pobrane języki:", response.data);
        setAvailableLanguages(response.data);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchQualificationCategories = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(`${API_BASE_URL}/qualificationcategory/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQualificationCategories(response.data);
        console.log("Pobrane kategorie kwalifikacji:", response.data);
      } catch (error) {
        console.error("Error fetching qualification categories:", error);
      }
    };
    fetchQualificationCategories();
  }, []);

  useEffect(() => {
    const fetchQualifications = async () => {
      if (!user?.accessToken) return;

      try {
        const token = localStorage.getItem("access_token");
        const typeResponse = await axios.get(`${API_BASE_URL}/qualificationtype/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const qualificationTypes = typeResponse.data;
        console.log("Pobrane typy kwalifikacji:", qualificationTypes);

        const categoryResponse = await axios.get(`${API_BASE_URL}/qualificationcategory/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const categories = categoryResponse.data;
        console.log("Pobrane kategorie:", categories);

        const qualificationsList = processQualifications(categories, qualificationTypes);
        setGlobalQualifications(qualificationsList);
        console.log("Przetworzone kwalifikacje:", qualificationsList);
      } catch (error) {
        console.error("Error fetching qualifications:", error);
      }
    };
    fetchQualifications();
  }, [user]);

  const processQualifications = (categories, qualificationTypes) => {
    const qualifications = qualificationTypes.map(qualType => {
      const category = categories.find(cat => cat.id === qualType.category);
      const subcategory = category?.subcategories.find(sub => sub.id === qualType.subcategory) || null;
      return {
        id: qualType.id,
        name: qualType.name,
        categoryId: qualType.category,
        categoryName: category ? category.name : "Other",
        subcategoryId: qualType.subcategory || null,
        subcategoryName: subcategory ? subcategory.name : null,
        certificate_number: "",
        expiry_date: "",
        certificate_file: null,
      };
    });
    return qualifications;
  };

  useEffect(() => {
    if (!editingUserId && globalQualifications.length > 0) {
      const qualifications = globalQualifications.map(qual => ({
        id: qual.id,
        name: qual.name,
        categoryId: qual.categoryId,
        categoryName: qual.categoryName,
        subcategoryId: qual.subcategoryId,
        subcategoryName: qual.subcategoryName,
        certificate_number: "",
        expiry_date: "",
        certificate_file: null,
      }));
      setInitialValues(prev => ({ ...prev, qualifications }));
    }
  }, [globalQualifications, editingUserId]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (editingUserId) {
        try {
          const token = localStorage.getItem("access_token");
          console.log("editingUserId", editingUserId)
          const response = await axios.get(`${API_BASE_URL}/users/${editingUserId}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const userData = response.data;
          console.log("Dane użytkownika do edycji:", userData);

          const qualifications = globalQualifications.map(qual => ({
            id: qual.id,
            name: qual.name,
            categoryId: qual.categoryId,
            categoryName: qual.categoryName,
            subcategoryId: qual.subcategoryId,
            subcategoryName: qual.subcategoryName,
            certificate_number: "",
            expiry_date: "",
            certificate_file: null,
          }));

          userData.qualifications?.forEach((userQual) => {
            const index = qualifications.findIndex(q => q.id === userQual.qualification_type);
            if (index > -1) {
              qualifications[index] = {
                ...qualifications[index],
                edited: userQual.id || null,
                qualification_type: userQual.qualification_type,
                certificate_number: userQual.certificate_number || "",
                expiry_date: userQual.expiry_date || "",
                certificate_file: userQual.certificate_file || null,
              };
            }
          });

          const languages = userData.languages?.map((lang) => ({
            id: lang.id,
            name: lang.language,
            level: lang.level,
            certificate: lang.certificate || null,
          })) || [];

          const updatedInitialValues = {
            ...initialValues,
            ...userData,
            profile_picture: userData.profile_picture || null,
            qualifications,
            languages,
          };

          console.log("Zaktualizowane wartości początkowe:", updatedInitialValues);
          setInitialValues(updatedInitialValues);
          setOriginalValues(updatedInitialValues);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };
    fetchUserDetails();
  }, [editingUserId, globalQualifications]);

  const getUpdatedFields = (current, original = {}) => {
    const updates = {};
    for (const key in current) {
      if (key === "languages") {
        console.log("Current.languages value:", current[key]);
        updates[key] = current[key];
        continue;
      }
      if (key === "username" || key === "inspector") {
        updates[key] = current[key];
      } else if (current[key] instanceof File) {
        updates[key] = current[key];
      } else if (typeof current[key] === "object" && current[key] !== null) {
        const nestedUpdates = getUpdatedFields(current[key], original[key] || {});
        if (Object.keys(nestedUpdates).length > 0) {
          updates[key] = nestedUpdates;
        }
      } else if (current[key] !== original[key]) {
        updates[key] = current[key];
      }
    }
    return updates;
  };

  const getChangedQualifications = (currentQualifications = [], originalQualifications = []) => {
    return currentQualifications.filter((qual, index) => {
      const originalQual = originalQualifications[index];
      const isNewQualification = !originalQual;
      const isEmptyQualification = !qual.certificate_number && !qual.expiry_date && !qual.certificate_file;

      if (isNewQualification && isEmptyQualification) return false;

      return (
        isNewQualification ||
        qual.certificate_number !== originalQual?.certificate_number ||
        qual.expiry_date !== originalQual?.expiry_date ||
        qual.certificate_file !== originalQual?.certificate_file
      );
    });
  };

  const getChangedLanguages = (currentLanguages = [], originalLanguages = []) => {
    return currentLanguages.filter((lang) => {
      const originalLang = originalLanguages.find((oLang) => oLang.id === lang.id);
      return (
        !originalLang ||
        lang.level !== originalLang.level ||
        lang.certificate !== originalLang.certificate
      );
    });
  };

  // Date formatting functions
  const formatDateForInput = (value) => {
    if (!value) return '';
    
    // If already in yyyy-MM-dd format, return as is
    if (value.includes('-') && value.split('-')[0].length === 4) {
      return value;
    }
    
    // Convert dd.MM.yyyy to yyyy-MM-dd
    if (value.includes('.')) {
      const [day, month, year] = value.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return value;
  };
  
  const formatDateForSubmit = (value) => {
    if (!value) return '';
    
    // If already in dd.MM.yyyy format, return as is
    if (value.includes('.')) {
      return value;
    }
    
    // Convert yyyy-MM-dd to dd.MM.yyyy
    if (value.includes('-')) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }
    
    return value;
  };
  
  // Legacy function for API submission - converts to ISO format
  const formatDate = (value) => {
    if (value) {
      // If in dd.MM.yyyy format, convert first
      if (value.includes('.')) {
        const [day, month, year] = value.split('.');
        const date = new Date(`${year}-${month}-${day}`);
        return date.toISOString().split("T")[0];
      }
      const date = new Date(value);
      return date.toISOString().split("T")[0];
    }
    return null;
  };

  // Dialog handlers
  const handleConfirmHistoricalQualifications = () => {
    setShowConfirmDialog(false);
    setSkipHistoricalWarning(true);
    // Re-submit the form, this time it will skip the warning
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handleCancelHistoricalQualifications = () => {
    setShowConfirmDialog(false);
    setPendingQualifications([]);
    setSkipHistoricalWarning(false);
  };

  // Fetch version history for a qualification
  const fetchQualificationHistory = async (qualEditedId) => {
    if (!qualEditedId) return;

    setQualificationHistory(prev => ({
      ...prev,
      [qualEditedId]: { ...prev[qualEditedId], loading: true }
    }));

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/userqualification/${qualEditedId}/?include_history=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let historyData = [];
      if (response.data.version_history) {
        historyData = response.data.version_history;
      } else {
        historyData = [{
          id: response.data.id,
          version: response.data.version || 1,
          expiry_date: response.data.expiry_date,
          certificate_number: response.data.certificate_number,
          certificate_file: response.data.certificate_file,
          is_current: response.data.is_current,
          created_at: response.data.created_at,
          superseded_at: response.data.superseded_at
        }];
      }

      setQualificationHistory(prev => ({
        ...prev,
        [qualEditedId]: { loading: false, show: true, data: historyData }
      }));
    } catch (error) {
      console.error("Error fetching qualification history:", error);
      setQualificationHistory(prev => ({
        ...prev,
        [qualEditedId]: { loading: false, show: true, data: [] }
      }));
    }
  };

  const toggleQualificationHistory = (qualEditedId) => {
    const current = qualificationHistory[qualEditedId];
    if (!current?.show) {
      fetchQualificationHistory(qualEditedId);
    } else {
      setQualificationHistory(prev => ({
        ...prev,
        [qualEditedId]: { ...prev[qualEditedId], show: false }
      }));
    }
  };

  const handleSave = async (values) => {
    const userApiUrl = editingUserId ? `${API_BASE_URL}/users/${editingUserId}/` : `${API_BASE_URL}/users/`;
    const method = editingUserId ? "put" : "post";

    try {
      const token = localStorage.getItem("access_token");
      const updatedFields = getUpdatedFields(values, originalValues || {});
      ["date_of_birth"].forEach((dateField) => {
        if (updatedFields[dateField]) {
          updatedFields[dateField] = formatDate(updatedFields[dateField]);
        }
      });
      console.log("UpdatedFields:", updatedFields);

      const userFormData = new FormData();
      Object.entries(updatedFields).forEach(([key, value]) => {
        if (value instanceof File) {
          userFormData.append(key, value);
        } else if (value !== null && value !== undefined) {
          userFormData.append(key, value);
        } else if (key === "profile_picture" && value === null) {
          userFormData.append("profile_picture", "");
        }
      });
      userFormData.append("is_active", 'true');

      console.log("UserFormData (before sending):");
      userFormData.forEach((value, key) => {
        console.log(key, value);
      });

      const userResponse = await axios({
        method,
        url: userApiUrl,
        data: userFormData,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      const userId = editingUserId || userResponse.data.id;
      console.log("User saved successfully:", userResponse.data);

      const changedLanguages = getChangedLanguages(values.languages, originalValues?.languages || []);
      for (const lang of changedLanguages) {
        try {
          const languageFormData = new FormData();
          languageFormData.append("user", userId);
          languageFormData.append("language", lang.name);
          languageFormData.append("level", lang.level);
          if (lang.certificate instanceof File) {
            languageFormData.append("certificate", lang.certificate);
          }

          const langMethod = lang.id ? "put" : "post";
          const langUrl = lang.id ? `${API_BASE_URL}/user-languages/${lang.id}/` : `${API_BASE_URL}/user-languages/`;

          console.log("Wysyłanie języka:", { method: langMethod, url: langUrl });
          await axios({
            method: langMethod,
            url: langUrl,
            data: languageFormData,
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });
          console.log(`Language ${lang.id ? "updated" : "created"}:`, lang);
        } catch (langError) {
          console.error("Error saving language:", langError.response?.data || langError.message);
        }
      }

      const changedQualifications = getChangedQualifications(values.qualifications, originalValues?.qualifications || []);
      console.log("Lista zmienionych kwalifikacji:", changedQualifications);

      // Check for historical qualifications being added to users who already have them
      const historicalQualifications = changedQualifications.filter(qual => {
        if (!qual.expiry_date) return false;

        // Parse the date (handle both formats: dd.MM.yyyy and yyyy-MM-dd)
        let expiryDate;
        if (qual.expiry_date.includes('.')) {
          const [day, month, year] = qual.expiry_date.split('.');
          expiryDate = new Date(year, month - 1, day);
        } else {
          expiryDate = new Date(qual.expiry_date);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Check if date is in the past
        if (expiryDate >= today) return false;

        // Check if user already has this qualification type
        const originalQual = originalValues?.qualifications?.find((oq) => oq.id === qual.id);
        return !!originalQual;
      });

      if (historicalQualifications.length > 0 && !skipHistoricalWarning) {
        // Show custom dialog instead of system confirm
        setPendingQualifications(historicalQualifications);
        setShowConfirmDialog(true);
        return; // Stop here and wait for user confirmation
      }

      for (const qual of changedQualifications) {
        try {
          const qualificationFormData = new FormData();
          const formattedExpiryDate = formatDate(qual.expiry_date);
          qualificationFormData.append("user", userId);
          qualificationFormData.append("qualification_type", qual.id);

          const isExistingQualification = !!qual.edited;
          const qualMethod = isExistingQualification ? "put" : "post";
          const qualUrl = isExistingQualification
            ? `${API_BASE_URL}/userqualification/${qual.edited}/`
            : `${API_BASE_URL}/userqualification/`;

          console.log("Czy kwalifikacja istnieje:", {
            id: qual.id,
            isExistingQualification,
            editedId: qual.edited || "Nowa kwalifikacja",
          });

          const originalQual = originalValues?.qualifications?.find((oq) => oq.id === qual.id) || null;
          if (!originalQual || qual.certificate_number !== originalQual.certificate_number) {
            qualificationFormData.append("certificate_number", qual.certificate_number || "");
          }
          if (!originalQual || formattedExpiryDate !== originalQual.expiry_date) {
            qualificationFormData.append("expiry_date", formattedExpiryDate || "");
          }
          if (qual.certificate_file instanceof File) {
            qualificationFormData.append("certificate_file", qual.certificate_file);
          }

          console.log("Wysyłanie kwalifikacji:", {
            method: qualMethod,
            url: qualUrl,
            data: Object.fromEntries(qualificationFormData.entries()),
          });

          await axios({
            method: qualMethod,
            url: qualUrl,
            data: qualificationFormData,
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });

          console.log(`Qualification ${isExistingQualification ? "updated" : "created"}:`, qual);
        } catch (qualError) {
          console.error("Error saving qualification:", qualError.response?.data || qualError.message);
        }
      }

      console.log("All changed qualifications saved successfully");

      // Reset the skip warning flag
      setSkipHistoricalWarning(false);

      // Show success message
      setSaveMessage({
        type: 'success',
        text: editingUserId ? 'User updated successfully!' : 'User created successfully!'
      });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 5000);

      onSave();

      // Refresh user data in the form after update
      if (editingUserId) {
        try {
          const response = await axios.get(`${API_BASE_URL}/users/${editingUserId}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const userData = response.data;

          const qualifications = globalQualifications.map(qual => ({
            id: qual.id,
            name: qual.name,
            categoryId: qual.categoryId,
            categoryName: qual.categoryName,
            subcategoryId: qual.subcategoryId,
            subcategoryName: qual.subcategoryName,
            certificate_number: "",
            expiry_date: "",
            certificate_file: null,
          }));

          userData.qualifications?.forEach((userQual) => {
            const index = qualifications.findIndex(q => q.id === userQual.qualification_type);
            if (index > -1) {
              qualifications[index] = {
                ...qualifications[index],
                edited: userQual.id || null,
                qualification_type: userQual.qualification_type,
                certificate_number: userQual.certificate_number || "",
                expiry_date: userQual.expiry_date || "",
                certificate_file: userQual.certificate_file || null,
              };
            }
          });

          const languages = userData.languages?.map((lang) => ({
            id: lang.id,
            name: lang.language,
            level: lang.level,
            certificate: lang.certificate || null,
          })) || [];

          const updatedInitialValues = {
            ...initialValues,
            ...userData,
            profile_picture: userData.profile_picture || null,
            qualifications,
            languages,
          };

          setInitialValues(updatedInitialValues);
          setOriginalValues(updatedInitialValues);
          console.log("User data refreshed in form after update");
        } catch (refreshError) {
          console.error("Error refreshing user data:", refreshError);
        }
      }
    } catch (error) {
      console.error("Error saving data:", error.response?.data || error.message);

      // Show error message
      let errorText = "An error occurred while saving the user.";
      if (error.response?.data?.error && error.response.data.error.includes("Constraint \"unique_email\" is violated")) {
        errorText = "Email address is already in use by another user.";
      } else if (error.response?.data?.email && Array.isArray(error.response.data.email)) {
        errorText = `Email error: ${error.response.data.email.join(', ')}`;
      } else if (error.response?.data?.company_email && Array.isArray(error.response.data.company_email)) {
        errorText = `Company email error: ${error.response.data.company_email.join(', ')}`;
      }

      setSaveMessage({
        type: 'error',
        text: errorText
      });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 7000);
    }
  };

  const handleDeleteLanguage = async (languageId) => {
    if (!languageId) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API_BASE_URL}/user-languages/${languageId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(`🗑️ Language ${languageId} deleted successfully`);
    } catch (error) {
      console.error(`❌ Error deleting language ${languageId}:`, error.response?.data || error.message);
    }
  };

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => {
      const newState = {};
      if (!prev[categoryId]) {
        newState[categoryId] = true;
      }
      return newState;
    });
  };

  const Sidebar = () => (
    <div className={`bg-steel-100 h-full overflow-y-auto max-h-[calc(90vh-3rem)] flex-shrink-0 ${
      showDocumentPreview ? 'w-full p-2' : 'w-64 p-4'
    }`}>
      <h3 className="text-lg font-semibold mb-4 text-slate-900">User Details</h3>
      <button
        onClick={() => {
          setSelectedSection("basic");
          setSaveMessage({ type: '', text: '' });
        }}
        className={`flex items-center w-full text-left py-2 px-3 text-slate-700 hover:bg-brand-50 rounded-xl transition-colors ${
          selectedSection === "basic" ? "bg-brand-50 text-brand-700 border-l-2 border-brand-500" : ""
        }`}
      >
        <User className="h-5 w-5 mr-2" /> Basic Info
      </button>
      <button
        onClick={() => {
          setSelectedSection("personal");
          setSaveMessage({ type: '', text: '' });
        }}
        className={`flex items-center w-full text-left py-2 px-3 text-slate-700 hover:bg-brand-50 rounded-xl transition-colors ${
          selectedSection === "personal" ? "bg-brand-50 text-brand-700 border-l-2 border-brand-500" : ""
        }`}
      >
        <Shield className="h-5 w-5 mr-2" /> Personal
      </button>
      <button
        onClick={() => {
          setSelectedSection("work");
          setSaveMessage({ type: '', text: '' });
        }}
        className={`flex items-center w-full text-left py-2 px-3 text-slate-700 hover:bg-brand-50 rounded-xl transition-colors ${
          selectedSection === "work" ? "bg-brand-50 text-brand-700 border-l-2 border-brand-500" : ""
        }`}
      >
        <Briefcase className="h-5 w-5 mr-2" /> Work
      </button>
      <h3 className="text-lg font-semibold mt-6 mb-2 text-slate-900">Qualifications</h3>
      {qualificationCategories
        .filter(cat => !["Personal", "Business"].includes(cat.name))
        .sort((a, b) => {
          if (a.name === "Other") return 1;
          if (b.name === "Other") return -1;
          return 0;
        })
        .map(cat => (
          <div key={cat.id}>
            <button
              onClick={() => {
                toggleCategory(cat.id);
                setSelectedSection({ type: "category", id: cat.id, name: cat.name });
                setSaveMessage({ type: '', text: '' });
              }}
              className={`flex items-center justify-between w-full text-left py-2 px-3 text-slate-700 hover:bg-brand-50 rounded-xl transition-colors ${
                selectedSection?.type === "category" && selectedSection?.id === cat.id ? "bg-brand-50 text-brand-700 border-l-2 border-brand-500" : ""
              }`}
            >
              <span>{cat.name}</span>
              {cat.subcategories?.length > 0 && (
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${openCategories[cat.id] ? "rotate-180" : ""}`}
                />
              )}
            </button>
            {cat.subcategories?.length > 0 && openCategories[cat.id] && (
              <div className="ml-4">
                {cat.subcategories.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => {
                      setSelectedSection({ type: "subcategory", id: sub.id, name: sub.name, parent: cat.name });
                      setSaveMessage({ type: '', text: '' });
                    }}
                    className={`w-full text-left py-2 px-3 text-slate-600 hover:bg-brand-50 rounded-xl transition-colors ${
                      selectedSection?.type === "subcategory" && selectedSection?.id === sub.id ? "bg-brand-50 text-brand-700" : ""
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );

  const renderQualifications = (categoryId, subcategoryId = null, setFieldValue, values) => {
    // Check if this is the PPE Inspector category
    const currentCategory = qualificationCategories.find(cat => cat.id === categoryId);
    const isPPEInspectorCategory = currentCategory && currentCategory.name === "PPE Inspector";
    
    return (
      <div className="space-y-2">
        {isPPEInspectorCategory && (
          <div className="mb-4 p-4 bg-brand-50 border border-brand-200 rounded-xl">
            <div className="flex items-center space-x-2">
              <Field name="inspector">
                {({ field }) => (
                  <input
                    type="checkbox"
                    id="inspector-checkbox"
                    {...field}
                    checked={values.inspector}
                    onChange={(e) => setFieldValue('inspector', e.target.checked)}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                  />
                )}
              </Field>
              <label htmlFor="inspector-checkbox" className="text-sm font-medium text-slate-900">
                Has inspector's qualifications
              </label>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Check this box if the user has the necessary qualifications to perform inspections.
            </p>
          </div>
        )}
        
        {values.qualifications
          .filter(qual => qual.categoryId === categoryId && (!subcategoryId || qual.subcategoryId === subcategoryId))
          .map((qual) => {
            const globalIndex = values.qualifications.findIndex(q => q.id === qual.id);
            const historyState = qual.edited ? qualificationHistory[qual.edited] : null;
            return (
              <div key={qual.id} className="border-b pb-2">
                <div className="grid grid-cols-5 gap-3 items-center">
                  <div className="col-span-5 font-semibold">{qual.name}</div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">Certificate Number</label>
                  <Field
                    name={`qualifications[${globalIndex}].certificate_number`}
                    placeholder="Certificate Number"
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">Expiry Date</label>
                  <Field name={`qualifications[${globalIndex}].expiry_date`}>
                    {({ field, form }) => (
                      <input
                        type="date"
                        value={formatDateForInput(field.value)}
                        onChange={(e) => {
                          // Store in dd.MM.yyyy format
                          form.setFieldValue(`qualifications[${globalIndex}].expiry_date`, formatDateForSubmit(e.target.value));
                        }}
                        className="w-full p-2 border rounded"
                      />
                    )}
                  </Field>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Certificate File</label>
                  <div className="flex items-center gap-1">
                    <div className="flex items-center flex-1 p-2 border rounded min-w-0">
                      <div className="flex-1 truncate">
                        {qual.certificate_file ? (
                          typeof qual.certificate_file === "string" ? (
                            <span className="text-brand-500 text-sm truncate">
                              {qual.certificate_file.split("/").pop()}
                            </span>
                          ) : (
                            <span className="text-slate-600 text-sm truncate">{values.qualifications[globalIndex].certificate_file_name}</span>
                          )
                        ) : (
                          <span className="text-slate-400 text-sm">No file</span>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        ref={(el) => (fileInputRefs.current[globalIndex] = el)}
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          if (file) {
                            if (file.size > MAX_FILE_SIZE) {
                              setSaveMessage({
                                type: 'error',
                                text: `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.`
                              });
                              event.target.value = '';
                              return;
                            }
                            setFieldValue(`qualifications[${globalIndex}].certificate_file`, file);
                            setFieldValue(`qualifications[${globalIndex}].certificate_file_name`, file.name);
                          }
                        }}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRefs.current[globalIndex]?.click()}
                        className="ml-1 px-2 py-1 bg-steel-200 hover:bg-steel-300 rounded-xl text-xs transition-colors"
                      >
                        {!qual.certificate_file ? "Add" : "Change"}
                      </button>
                    </div>
                    {qual.certificate_file && typeof qual.certificate_file === "string" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = qual.certificate_file;
                            link.download = qual.certificate_file.split("/").pop();
                            link.click();
                          }}
                          className="p-1.5 text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewDocument({
                              url: qual.certificate_file,
                              name: qual.certificate_file.split("/").pop()
                            });
                            setShowDocumentPreview(true);
                          }}
                          className="p-1.5 text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {qual.edited && (
                      <button
                        type="button"
                        onClick={() => toggleQualificationHistory(qual.edited)}
                        className={`p-1.5 rounded-xl transition-colors ${historyState?.show ? 'bg-brand-100 text-brand-700' : 'text-slate-500 hover:bg-steel-100'}`}
                        title="Version History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                </div>

                {/* Version History Section - Compact with preview option */}
                {historyState?.show && (
                  <div className="mt-2 ml-2 p-2 bg-steel-50 rounded-xl border border-steel-200 text-xs">
                    <div className="font-medium text-slate-700 mb-1">Previous versions:</div>
                    {historyState.loading ? (
                      <span className="text-slate-500">Loading...</span>
                    ) : historyState.data?.length === 0 ? (
                      <span className="text-slate-500">No previous versions</span>
                    ) : (
                      <div className="space-y-1">
                        {historyState.data.map((version) => (
                          <div
                            key={version.id}
                            className={`flex items-center justify-between p-1.5 rounded-xl ${
                              version.is_current ? 'bg-brand-50 border border-brand-200' : 'bg-white border border-steel-100'
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`font-medium ${version.is_current ? 'text-brand-700' : 'text-slate-700'}`}>
                                v{version.version}{version.is_current && ' (current)'}
                              </span>
                              {version.certificate_number && <span className="text-slate-500">#{version.certificate_number}</span>}
                              {version.expiry_date && <span className="text-slate-500">{new Date(version.expiry_date).toLocaleDateString()}</span>}
                            </div>
                            {version.certificate_file && (
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => previewDocumentWithAuth(
                                    version.certificate_file,
                                    `v${version.version} - ${version.certificate_file.split("/").pop()}`
                                  )}
                                  className="p-1 text-brand-600 hover:bg-brand-100 rounded-xl transition-colors"
                                  title="Preview this version"
                                  disabled={previewLoading}
                                >
                                  <Eye className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem("access_token");
                                      const response = await fetch(version.certificate_file, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      const blob = await response.blob();
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = `v${version.version}-${version.certificate_file.split("/").pop()}`;
                                      link.click();
                                      URL.revokeObjectURL(url);
                                    } catch (error) {
                                      console.error("Download error:", error);
                                    }
                                  }}
                                  className="p-1 text-brand-600 hover:bg-brand-100 rounded-xl transition-colors"
                                  title="Download this version"
                                >
                                  <Download className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  if (!isCreatorOpen) return null;

return (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-50 overflow-hidden">
    <div className={`bg-white rounded-lg p-6 w-full max-h-[95vh] flex relative transition-all duration-300 ${
      showDocumentPreview ? 'max-w-[98vw]' : 'max-w-[95vw]'
    }`}>
      {/* Section 1: Sidebar - User Details */}
      <div className={`${showDocumentPreview ? 'w-[15%]' : 'w-64'} transition-all duration-300`}>
        <Sidebar />
      </div>
      
      {/* Section 2: Form Content */}
      <div className={`flex-1 mb-16 overflow-y-auto transition-all duration-300 ${
        showDocumentPreview ? 'w-[45%] pl-1 pr-2 py-4' : 'p-4'
      }`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{editingUserId ? "Edit User" : "Create New User"}</h2>

        </div>

        {/* Success/Error/Info Message */}
        {saveMessage.text && (
          <div className={`mb-4 p-4 rounded-lg border-l-4 ${
            saveMessage.type === 'success'
              ? 'bg-green-50 border-green-500 text-green-800'
              : saveMessage.type === 'info'
              ? 'bg-brand-50 border-brand-500 text-brand-800'
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <div className="flex items-center">
              {saveMessage.type === 'success' ? (
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : saveMessage.type === 'info' ? (
                <svg className="w-5 h-5 mr-2 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="font-medium">{saveMessage.text}</span>
            </div>
          </div>
        )}

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={getValidationSchema(editingUserId)}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form id="formik-form">
              {selectedSection === "basic" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Username</label>
                    <Field
                      name="username"
                      placeholder="Username"
                      disabled={!!editingUserId}
                      className={`w-full p-2 border rounded ${errors.username && touched.username ? "border-red-500" : ""}`}
                    />
                    {errors.username && touched.username && <div className="text-red-500 text-sm">{errors.username}</div>}
                  </div>
                  {editingUserId && (
                    <div>
                      <label className="block text-sm font-medium mb-1">User Email (Personal)</label>
                      <Field
                        name="email"
                        placeholder="Personal Email"
                        type="email"
                        disabled
                        className="w-full p-2 border rounded bg-slate-100 text-slate-500 cursor-not-allowed"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Company Email</label>
                    <Field
                      name="company_email"
                      placeholder="Company Email"
                      type="email"
                      className={`w-full p-2 border rounded ${errors.company_email && touched.company_email ? "border-red-500" : ""}`}
                    />
                    {errors.company_email && touched.company_email && <div className="text-red-500 text-sm">{errors.company_email}</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name</label>
                    <Field
                      name="first_name"
                      placeholder="First Name"
                      className={`w-full p-2 border rounded ${errors.first_name && touched.first_name ? "border-red-500" : ""}`}
                    />
                    {errors.first_name && touched.first_name && <div className="text-red-500 text-sm">{errors.first_name}</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name</label>
                    <Field
                      name="last_name"
                      placeholder="Last Name"
                      className={`w-full p-2 border rounded ${errors.last_name && touched.last_name ? "border-red-500" : ""}`}
                    />
                    {errors.last_name && touched.last_name && <div className="text-red-500 text-sm">{errors.last_name}</div>}
                  </div>
                  {!editingUserId && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Password</label>
                        <Field
                          name="password"
                          placeholder="Password"
                          type="password"
                          className={`w-full p-2 border rounded ${errors.password && touched.password ? "border-red-500" : ""}`}
                        />
                        {errors.password && touched.password && <div className="text-red-500 text-sm">{errors.password}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Confirm Password</label>
                        <Field
                          name="confirm_password"
                          placeholder="Confirm Password"
                          type="password"
                          className={`w-full p-2 border rounded ${errors.confirm_password && touched.confirm_password ? "border-red-500" : ""}`}
                        />
                        {errors.confirm_password && touched.confirm_password && <div className="text-red-500 text-sm">{errors.confirm_password}</div>}
                      </div>
                    </>
                  )}
                </div>
              )}

              {selectedSection === "personal" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <ProfilePictureUpload
                        currentImage={values.profile_picture}
                        onImageChange={(file) => setFieldValue("profile_picture", file)}
                        onImageRemove={() => setFieldValue("profile_picture", null)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Date of Birth</label>
                      <Field name="date_of_birth">
                        {({ field, form }) => (
                          <input
                            type="date"
                            value={formatDateForInput(field.value)}
                            onChange={(e) => {
                              // Store in dd.MM.yyyy format
                              form.setFieldValue('date_of_birth', formatDateForSubmit(e.target.value));
                            }}
                            className="w-full p-2 border rounded"
                          />
                        )}
                      </Field>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone Number</label>
                      <Field name="phone_number" placeholder="Phone Number" className="w-full p-2 border rounded" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Personal Address</label>
                      <Field as="textarea" name="personal_address" placeholder="e.g., Szczecin, ul. Parkowa 12/5, 70-001" className="w-full p-2 border rounded" rows="3" />
                      <p className="text-xs text-slate-500 mt-1">Format: City, street number/apartment, postal code (e.g., Szczecin, ul. Parkowa 12/5, 70-001)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Closest Airport</label>
                      <Field name="closest_airport" placeholder="Closest Airport" className="w-full p-2 border rounded" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">WINDA ID</label>
                      <div className="flex gap-2">
                        <Field
                          name="winda_id"
                          placeholder="e.g., TT098322DE"
                          className="flex-1 p-2 border rounded"
                        />
                        {values.winda_id && (
                          <button
                            type="button"
                            onClick={async () => {
                              setSaveMessage({ type: 'info', text: 'Fetching certificates from WINDA...' });
                              try {
                                const token = localStorage.getItem("access_token");
                                const response = await axios.post(
                                  `${API_BASE_URL}/winda/fetch-certificates/`,
                                  {
                                    winda_id: values.winda_id,
                                    user_id: editingUserId  // Send the user being edited, not the logged-in user
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );

                                if (response.data.success) {
                                  // Show results in modal
                                  setWindaResultsModal({
                                    open: true,
                                    data: response.data
                                  });

                                  // Refresh user data if editing
                                  if (editingUserId) {
                                    const userResponse = await axios.get(`${API_BASE_URL}/users/${editingUserId}/`, {
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    const userData = userResponse.data;

                                    const qualifications = globalQualifications.map(qual => ({
                                      id: qual.id,
                                      name: qual.name,
                                      categoryId: qual.categoryId,
                                      categoryName: qual.categoryName,
                                      subcategoryId: qual.subcategoryId,
                                      subcategoryName: qual.subcategoryName,
                                      certificate_number: "",
                                      expiry_date: "",
                                      certificate_file: null,
                                    }));

                                    userData.qualifications?.forEach((userQual) => {
                                      const index = qualifications.findIndex(q => q.id === userQual.qualification_type);
                                      if (index > -1) {
                                        qualifications[index] = {
                                          ...qualifications[index],
                                          edited: userQual.id || null,
                                          qualification_type: userQual.qualification_type,
                                          certificate_number: userQual.certificate_number || "",
                                          expiry_date: userQual.expiry_date || "",
                                          certificate_file: userQual.certificate_file || null,
                                        };
                                      }
                                    });

                                    const updatedInitialValues = {
                                      ...initialValues,
                                      ...userData,
                                      qualifications,
                                    };

                                    setInitialValues(updatedInitialValues);
                                    setOriginalValues(updatedInitialValues);
                                  }
                                } else {
                                  setSaveMessage({
                                    type: 'error',
                                    text: response.data.message || 'Failed to fetch certificates'
                                  });
                                }
                              } catch (error) {
                                console.error("Error fetching WINDA certificates:", error);
                                const errorCode = error.response?.data?.error_code;
                                const errorMessage = error.response?.data?.message;

                                let displayMessage;
                                switch (errorCode) {
                                  case 'USER_NOT_FOUND':
                                    displayMessage = `User with WINDA ID "${values.winda_id}" not found in WINDA database. Please verify the ID is correct.`;
                                    break;
                                  case 'AUTH_FAILED':
                                    displayMessage = 'WINDA API authentication failed. Please contact administrator to check API configuration.';
                                    break;
                                  case 'TIMEOUT':
                                    displayMessage = 'WINDA API request timed out. Please try again later.';
                                    break;
                                  case 'NOT_CONFIGURED':
                                    displayMessage = 'WINDA API is not configured. Please contact administrator.';
                                    break;
                                  default:
                                    displayMessage = errorMessage || 'Error fetching certificates from WINDA API';
                                }

                                setSaveMessage({
                                  type: 'error',
                                  text: displayMessage
                                });
                              }
                              setTimeout(() => setSaveMessage({ type: '', text: '' }), 15000);
                            }}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 whitespace-nowrap"
                          >
                            Fetch Certificates
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Enter WINDA ID to automatically fetch and populate certificates for this user
                      </p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">Personal Qualifications</h3>
                    {renderQualifications(
                      qualificationCategories.find(cat => cat.name === "Personal")?.id,
                      null,
                      setFieldValue,
                      values
                    )}
                  </div>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">Languages</h3>
                    <FieldArray name="languages">
                      {({ push, remove }) => (
                        <div>
                          {values.languages.map((lang, index) => (
                            <div key={index} className="flex items-center space-x-4 mb-2">
                              <div className="w-1/4">
                                <label className="text-sm font-medium mb-1">Choose language</label>
                                <Field
                                  as="select"
                                  name={`languages.${index}.name`}
                                  className={`w-full p-2 border rounded ${
                                    errors?.languages?.[index]?.name && touched?.languages?.[index]?.name ? "border-red-500" : ""
                                  }`}
                                  disabled={!!lang.id}
                                >
                                  <option value="" disabled>Select Language</option>
                                  {availableLanguages.map((language) => (
                                    <option key={language.id} value={language.id}>{language.name}</option>
                                  ))}
                                </Field>
                                {errors?.languages?.[index]?.name && touched?.languages?.[index]?.name && (
                                  <div className="text-red-500 text-sm">{errors.languages[index].name}</div>
                                )}
                              </div>
                              <div className="w-1/4">
                                <label className="text-sm font-medium mb-1">Level</label>
                                <Field
                                  as="select"
                                  name={`languages.${index}.level`}
                                  className={`w-full p-2 border rounded ${
                                    errors?.languages?.[index]?.level && touched?.languages?.[index]?.level ? "border-red-500" : ""
                                  }`}
                                >
                                  <option value="" disabled>Select Level</option>
                                  {LANGUAGE_LEVEL_CHOICES.map((level) => (
                                    <option key={level.value} value={level.value}>{level.label}</option>
                                  ))}
                                </Field>
                                {errors?.languages?.[index]?.level && touched?.languages?.[index]?.level && (
                                  <div className="text-red-500 text-sm">{errors.languages[index].level}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1">
                                  <label className="text-sm font-medium mb-1">Certificate File</label>
                                  <div className="flex items-center w-full p-2 border rounded">
                                    <div className="flex-1">
                                      {lang.certificate ? (
                                        typeof lang.certificate === "string" ? (
                                          <span className="text-brand-500 text-sm">
                                            {lang.certificate.split("/").pop()}
                                          </span>
                                        ) : (
                                          <span className="text-slate-600 text-sm">{values.languages[index].certificate_name}</span>
                                        )
                                      ) : (
                                        <span className="text-slate-400 text-sm">No file uploaded</span>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      ref={el => (languageFileInputRefs.current[index] = el)}
                                      onChange={(event) => {
                                        const file = event.currentTarget.files[0];
                                        if (file) {
                                          if (file.size > MAX_FILE_SIZE) {
                                            setSaveMessage({
                                              type: 'error',
                                              text: `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.`
                                            });
                                            event.target.value = '';
                                            return;
                                          }
                                          setFieldValue(`languages.${index}.certificate`, file);
                                          setFieldValue(`languages.${index}.certificate_name`, file.name);
                                        }
                                      }}
                                      className="hidden"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => languageFileInputRefs.current[index]?.click()}
                                      className="ml-2 px-3 py-1 bg-steel-200 hover:bg-steel-300 rounded-xl text-sm transition-colors"
                                    >
                                      {!lang.certificate ? "Add" : typeof lang.certificate === "string" ? "Update" : "Change"}
                                    </button>
                                  </div>
                                </div>
                                {lang.certificate && typeof lang.certificate === "string" && (
                                  <div className="flex gap-1 mt-6">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = lang.certificate;
                                        link.download = lang.certificate.split("/").pop();
                                        link.click();
                                      }}
                                      className="p-2 text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded-xl border border-brand-200 transition-colors"
                                      title="Download certificate"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPreviewDocument({
                                          url: lang.certificate,
                                          name: lang.certificate.split("/").pop()
                                        });
                                        setShowDocumentPreview(true);
                                      }}
                                      className="p-2 text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded-xl border border-brand-200 transition-colors"
                                      title="Preview certificate"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (lang.id) handleDeleteLanguage(lang.id);
                                  remove(index);
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push({ name: "", level: "", certificate: null })}
                            className="mt-2 px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
                          >
                            Add Language
                          </button>
                          {errors.languages && typeof errors.languages === "string" && (
                            <div className="text-red-500 text-sm mt-2">{errors.languages}</div>
                          )}
                        </div>
                      )}
                    </FieldArray>
                  </div>
                </>
              )}

              {selectedSection === "work" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Company Name</label>
                      <Field name="your_company_name" placeholder="Company Name" className="w-full p-2 border rounded" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Company Address</label>
                      <Field as="textarea" name="company_address" placeholder="Company Address" className="w-full p-2 border rounded" rows="3" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax ID (NIP)</label>
                      <Field name="tax_id" placeholder="Polish Tax ID (NIP)" className="w-full p-2 border rounded" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">EU VAT ID</label>
                      <Field name="eu_tax_id" placeholder="EU VAT ID" className="w-full p-2 border rounded" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax ID FI (Finnish)</label>
                      <Field name="tax_id_fi" placeholder="Finnish Tax ID" className="w-full p-2 border rounded" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">Business Qualifications</h3>
                    {renderQualifications(
                      qualificationCategories.find(cat => cat.name === "Business")?.id,
                      null,
                      setFieldValue,
                      values
                    )}
                  </div>
                </>
              )}

              {selectedSection?.type === "category" && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-2">{selectedSection.name} Qualifications</h3>
                  {qualificationCategories.find(cat => cat.id === selectedSection.id)?.subcategories.length > 0 ? (
                    <p className="text-slate-500">Please select a subcategory to view qualifications.</p>
                  ) : (
                    renderQualifications(selectedSection.id, null, setFieldValue, values)
                  )}
                </div>
              )}

              {selectedSection?.type === "subcategory" && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-2">{selectedSection.parent} - {selectedSection.name} Qualifications</h3>
                  {renderQualifications(
                    qualificationCategories.find(cat => cat.name === selectedSection.parent)?.id,
                    selectedSection.id,
                    setFieldValue,
                    values
                  )}
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>

      {/* Przyciski w prawym dolnym rogu modalu */}
      <div className="absolute bottom-6 right-10 flex space-x-2 z-10">
        <button
          type="button"
          onClick={() => setIsCreatorOpen(false)}
          className="px-4 py-2 border border-steel-300 font-semibold rounded-xl hover:bg-steel-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="formik-form"
          className="px-4 py-2 font-semibold bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20 transition-colors"
        >
          {editingUserId ? "Update User" : "Create User"}
        </button>
      </div>
      
      {/* Section 3: Document Preview (integrated into main modal) */}
      {showDocumentPreview && previewDocument && (
        <div className="w-[40%] border-l border-steel-200 bg-steel-50 flex flex-col transition-all duration-300">
          <div className="flex items-center justify-between p-4 border-b border-steel-200 bg-white">
            <h3 className="text-lg font-semibold text-slate-900">Document Preview: {previewDocument.name}</h3>
            <button
              onClick={() => {
                if (previewDocument?.isBlob) {
                  URL.revokeObjectURL(previewDocument.url);
                }
                setShowDocumentPreview(false);
                setPreviewDocument(null);
              }}
              className="text-slate-500 hover:text-slate-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div className="flex-1 p-4 bg-white overflow-auto">
            {previewDocument.url.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={previewDocument.url}
                className="w-full h-full border border-steel-200 rounded-xl"
                title={`Preview: ${previewDocument.name}`}
              />
            ) : (
              <img
                src={previewDocument.url}
                alt={previewDocument.name}
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div className="p-4 border-t border-steel-200 bg-steel-50">
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDocument.url;
                link.download = previewDocument.name;
                link.click();
              }}
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 flex items-center gap-2 shadow-lg shadow-brand-500/20 transition-colors"
            >
              <Download className="h-4 w-4" />
              Download Document
            </button>
          </div>
        </div>
      )}

      {/* Historical Qualifications Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Historical Qualifications Warning
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-4">
              <p className="font-medium text-slate-900">
                You are adding historical qualifications with expiry dates in the past:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-700">
                {pendingQualifications.map((qual, index) => (
                  <li key={index}>{qual.name}</li>
                ))}
              </ul>
              <p className="text-slate-700">
                Our system versions qualifications automatically. Adding a new qualification will create a new version
                and set it as the current version, while the old one will be marked as superseded.
              </p>
              <p className="font-medium text-slate-900">
                Do you want to continue?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <button
              onClick={handleCancelHistoricalQualifications}
              className="flex-1 px-4 py-2 bg-steel-200 text-slate-800 rounded-md hover:bg-steel-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmHistoricalQualifications}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-medium"
            >
              Continue
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WINDA Results Modal */}
      <Dialog open={windaResultsModal.open} onOpenChange={(open) => setWindaResultsModal({ ...windaResultsModal, open })}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              WINDA Certificates Import Results
            </DialogTitle>
            <DialogDescription>
              {windaResultsModal.data && (
                <div className="flex gap-6 mt-2">
                  <span className="text-slate-700">Found: <strong>{windaResultsModal.data.certificates_count}</strong> certificates</span>
                  <span className="text-green-600">Created: <strong>{windaResultsModal.data.created_count}</strong></span>
                  <span className="text-brand-600">Updated: <strong>{windaResultsModal.data.updated_count}</strong></span>
                  <span className="text-slate-500">Skipped: <strong>{windaResultsModal.data.skipped_count}</strong></span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          {windaResultsModal.data && (
            <div className="flex-1 overflow-auto">
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column - Created */}
                <div className="space-y-2">
                  {windaResultsModal.data.created?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-green-100 px-3 py-2 border-b">
                        <h4 className="font-semibold text-green-800 flex items-center gap-1 text-sm">
                          <span>✓</span> Created ({windaResultsModal.data.created.length})
                        </h4>
                      </div>
                      <div className="divide-y">
                        {windaResultsModal.data.created.map((cert, idx) => (
                          <div key={idx} className="px-3 py-2 hover:bg-green-50/50 text-sm">
                            <div className="font-medium text-slate-900">{cert.qualification_name}</div>
                            <div className="flex gap-4 text-xs text-slate-600 mt-1">
                              <span className="font-mono bg-steel-100 px-1 rounded">{cert.course_code}</span>
                              <span>Expires: {cert.expiry_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skipped - Left column bottom */}
                  {windaResultsModal.data.skipped?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-steel-100 px-3 py-2 border-b">
                        <h4 className="font-semibold text-slate-700 flex items-center gap-1 text-sm">
                          <span>○</span> Skipped ({windaResultsModal.data.skipped.length})
                        </h4>
                      </div>
                      <div className="divide-y">
                        {windaResultsModal.data.skipped.map((cert, idx) => (
                          <div key={idx} className="px-3 py-2 hover:bg-steel-50/50 text-sm">
                            <div className="font-medium text-slate-700">{cert.qualification_name || cert.course_title || '-'}</div>
                            <div className="flex gap-4 text-xs text-slate-500 mt-1">
                              <span className="font-mono bg-steel-100 px-1 rounded">{cert.course_code}</span>
                              <span className="italic">{cert.reason}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Updated */}
                <div className="space-y-2">
                  {windaResultsModal.data.updated?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-brand-100 px-3 py-2 border-b">
                        <h4 className="font-semibold text-brand-800 flex items-center gap-1 text-sm">
                          <span>↻</span> Updated ({windaResultsModal.data.updated.length})
                        </h4>
                      </div>
                      <div className="divide-y">
                        {windaResultsModal.data.updated.map((cert, idx) => (
                          <div key={idx} className="px-3 py-2 hover:bg-brand-50/50 text-sm">
                            <div className="font-medium text-slate-900">{cert.qualification_name}</div>
                            <div className="flex gap-4 text-xs mt-1">
                              <span className="font-mono bg-steel-100 px-1 rounded">{cert.course_code}</span>
                              <span className="text-slate-400 line-through">{cert.old_expiry_date}</span>
                              <span className="text-brand-600 font-medium">{cert.new_expiry_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty states for right column */}
                  {windaResultsModal.data.updated?.length === 0 && windaResultsModal.data.created?.length > 0 && (
                    <div className="border rounded-lg p-4 text-center text-slate-400 text-sm">
                      No certificates were updated
                    </div>
                  )}
                </div>
              </div>

              {/* No changes */}
              {windaResultsModal.data.created?.length === 0 &&
               windaResultsModal.data.updated?.length === 0 &&
               windaResultsModal.data.skipped?.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No certificates were processed.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 mt-4">
            <button
              onClick={() => setWindaResultsModal({ open: false, data: null })}
              className="px-6 py-2 bg-steel-600 text-white rounded-xl hover:bg-steel-700 transition-colors"
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  </div>
);
};

export default AddOrEditUserForm;